import { __assign, __rest } from "tslib";
import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
/**
 * For some reason eslint is unable to resolve these named exports from
 * react-select.  Disabling the rule for imports from this library to prevent
 * lint errors
 */
/* eslint-disable import/named */
import Select, { components, } from 'react-select';
/* eslint-enable import/named */
import Icon from './Icon';
import './AdvancedSelect.scss';
var Sizes = {
    SMALL: 'small',
    MEDIUM: 'medium'
};
/**
 * We're defining multiple components in this file because they will exclusively be
 * used with the Select component defined at the end of this file.
 *
 * Most of these components consist of a simple `className` injection to target with
 * CSS and override react-select's default styling
 *
 * This (AdvancedSelect) component is also intended to replace `SelectMenu` and should be preferred
 * and used for net-new code.
 *
 * NOTE - This component does not yet support ALL of the same features as `SelectMenu` and therefore
 * may not be appropriate for all net-new code.  Namely, this component does not support:
 *    - Headings within options list
 *    - Custom width sizing to match form styles
 *    - Fine-grained control over filtering
 */
/**
 * react-select uses Emotion for CSS-in-JS styles, and provides no way
 * to disable it. You can override each style via functions that return
 * style objects, so this is a hack to make every style function return
 * an empty object. https://github.com/JedWatson/react-select/issues/2872
 */
var styleProxy = new Proxy({}, {
    get: function () { return function (_style) {
        // Use the following console statement to inspect the native styles for debugging
        // console.log(_style.label, _style);
        // The menu can be forced open to inspect its styles via a prop of `menuIsOpen={true}`
        return null;
    }; }
});
/**
 * The Control component is the parent container that acts as the button
 * when interacting with the AdvancedSelect conponent.  It is the equivalent
 * of the <summary> html tag and ensures the primary elements of the component
 * are interactive including:
 *    Any prepended component
 *    The filter input
 *    The DropdownIndicator
 */
var Control = function (_a) {
    var children = _a.children, isFocused = _a.isFocused, selectProps = _a.selectProps, props = __rest(_a, ["children", "isFocused", "selectProps"]);
    var prependComponent = selectProps.prependComponent;
    var classes = classnames('AdvancedSelect__control', {
        'AdvancedSelect__control--isFocused': isFocused
    });
    return (React.createElement(components.Control, __assign({ className: classes, isFocused: isFocused, selectProps: selectProps }, props),
        prependComponent ? (React.createElement("span", { className: 'AdvancedSelect__icon' }, prependComponent)) : null,
        children));
};
Control.propTypes = {
    children: PropTypes.node,
    isFocused: PropTypes.bool.isRequired,
    selectProps: PropTypes.shape({
        prependComponent: PropTypes.node
    }).isRequired
};
var SingleValue = function (props) { return (React.createElement(components.SingleValue, __assign({ className: 'AdvancedSelect__value' }, props))); };
var ValueContainer = function (props) { return (React.createElement(components.ValueContainer, __assign({ className: classnames('AdvancedSelect__valueContainer', {
        'AdvancedSelect__valueContainer--withIcon': Boolean(props.selectProps.prependComponent)
    }) }, props))); };
ValueContainer.propTypes = {
    selectProps: PropTypes.shape({
        prependComponent: PropTypes.node
    })
};
var Input = function (props) { return React.createElement(components.Input, __assign({ className: 'AdvancedSelect__input' }, props)); };
var Placeholder = function (props) { return (React.createElement(components.Placeholder, __assign({ className: 'AdvancedSelect__placeholder' }, props))); };
var DropdownIndicator = function (props) { return (React.createElement(components.DropdownIndicator, __assign({ className: 'AdvancedSelect__dropdownIcon' }, props),
    React.createElement(Icon, { "aria-hidden": true, type: 'dropdown' }))); };
var ClearIndicator = function (props) { return (React.createElement(components.ClearIndicator, __assign({ className: 'AdvancedSelect__clearIcon' }, props),
    React.createElement(Icon, { "aria-hidden": true, type: 'close' }))); };
var IndicatorsContainer = function (props) { return (React.createElement(components.IndicatorsContainer, __assign({ className: 'AdvancedSelect__indicatorsContainer' }, props))); };
var Option = function (_a) {
    var isFocused = _a.isFocused, isSelected = _a.isSelected, props = __rest(_a, ["isFocused", "isSelected"]);
    var classes = classnames('AdvancedSelect__option', {
        'AdvancedSelect__option--isFocused': isFocused,
        'AdvancedSelect__option--isSelected': isSelected
    });
    return (React.createElement(components.Option, __assign({ className: classes, isFocused: isFocused, isSelected: isSelected }, props)));
};
Option.propTypes = {
    isFocused: PropTypes.bool.isRequired,
    isSelected: PropTypes.bool.isRequired
};
var Menu = function (props) { return (React.createElement(components.Menu, __assign({ className: 'AdvancedSelect__menu' }, props))); };
var MenuList = function (props) { return (React.createElement(components.MenuList, __assign({ className: 'AdvancedSelect__menuList' }, props))); };
var isTAdvancedSelectOption = function (option) {
    return Boolean(option === null || option === void 0 ? void 0 : option.value);
};
var AdvancedSelect = function (_a) {
    var className = _a.className, errorMessage = _a.errorMessage, isRequired = _a.isRequired, label = _a.label, onChange = _a.onChange, options = _a.options, size = _a.size, value = _a.value, props = __rest(_a, ["className", "errorMessage", "isRequired", "label", "onChange", "options", "size", "value"]);
    var containerClassNames = classnames('AdvancedSelect', "AdvancedSelect--".concat(size), className);
    var selectClassNames = classnames({
        'AdvancedSelect__button--isInvalid': Boolean(errorMessage)
    });
    /**
     * When isClearable is true AND the user manually clears the value the option
     * passed to onChange will be null.  When a defaultValue is provided and the
     * selected option is manually cleared, the AdvancedSelect will always show
     * the default value (i.e. there's no way to actually clear the selected value)
     *
     * This boolean flag is used to determine whether or not a default value should
     * be displayed OR if the AdvancedSelect should render without a selected option
     */
    var _b = useState(false), didClearOptionManually = _b[0], setDidClearOptionManually = _b[1];
    var handleChange = function (option) {
        onChange((option === null || option === void 0 ? void 0 : option.value) || null);
        if (option === null) {
            setDidClearOptionManually(true);
        }
        else if (didClearOptionManually) {
            setDidClearOptionManually(false);
        }
    };
    var selectId = useRef();
    var getSelectedValue = function () {
        var _a;
        /**
         * We intentionally return null here if no option is found to fix a bug
         * with AdvancedSelect's integration with TemporaryBrowserData via its
         * Form component.  Array.find returns undefined which does not trigger
         * a rerender of react-select's Select component however null does
         */
        return ((_a = options.find(function (option) {
            if (value) {
                return isTAdvancedSelectOption(value)
                    ? option.value === value.value
                    : option.value === value;
            }
            else if (props.defaultValue && !didClearOptionManually) {
                // Only display a matching default value if value does not exist
                return isTAdvancedSelectOption(props.defaultValue)
                    ? option.value === props.defaultValue.value
                    : option.value === props.defaultValue;
            }
            return null;
        })) !== null && _a !== void 0 ? _a : null);
    };
    return (React.createElement("div", { className: containerClassNames },
        label ? (React.createElement("label", { className: 'AdvancedSelect__label', htmlFor: selectId.current },
            isRequired ? React.createElement("span", { "aria-hidden": 'true' }, "* ") : null,
            label)) : null,
        React.createElement(Select, __assign({ backspaceRemovesValue: true, className: selectClassNames, components: {
                ClearIndicator: ClearIndicator,
                Control: Control,
                DropdownIndicator: DropdownIndicator,
                IndicatorSeparator: null,
                IndicatorsContainer: IndicatorsContainer,
                Input: Input,
                Menu: Menu,
                MenuList: MenuList,
                Option: Option,
                Placeholder: Placeholder,
                SingleValue: SingleValue,
                ValueContainer: ValueContainer
            }, onChange: handleChange, options: options, ref: selectId, required: isRequired, styles: styleProxy, value: getSelectedValue() }, props)),
        errorMessage ? (React.createElement("div", { className: 'AdvancedSelect__errorMessage', role: 'alert' }, errorMessage)) : null));
};
AdvancedSelect.defaultProps = {
    isClearable: false,
    isDisabled: false,
    isRequired: false,
    onBlur: function () { },
    options: [],
    placeholder: 'Select...',
    size: Sizes.MEDIUM
};
AdvancedSelect.propTypes = {
    className: PropTypes.string,
    errorMessage: PropTypes.node,
    isClearable: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isRequired: PropTypes.bool,
    label: PropTypes.node,
    onBlur: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })),
    placeholder: PropTypes.string,
    prependComponent: PropTypes.node,
    size: PropTypes.oneOf(Object.values(Sizes)),
    /*
     * The selected value.
     */
    value: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.shape({
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        }),
    ])
};
export default AdvancedSelect;
//# sourceMappingURL=AdvancedSelect.js.map